import {memo} from 'react';

import {useSelector} from 'react-redux';

import {
  selectA11yContrast,
  selectIsTrackerUrlEmpty,
  selectOrderSuccessData,
  selectUserLastOrderScooberJob,
} from '~/shared/store/selectors';
import {EMPTY_OBJECT} from '~/shared/consts/commonConsts';
import {DeliveryMethods} from '~/shared/consts/restaurantConsts';
import {getTimeRangeLabel, isValidTimeRangeString} from '~/shared/utils/timeRangeHelper';
import {getDeliveryMethodFromAPIString} from '~/shared/utils/deliveryMethod';

import useIsShowMap from '../hooks/useIsShowMap';
import useIsShowDeliveryTrackerOrLink from '../hooks/useIsShowDeliveryTrackerOrLink';
import VoucherCardOrderComplete from '../VoucherCardOrderComplete';

import {InProgressTracker, TrackerVariants} from './InProgressTracker';
import OrderComplete from './OrderComplete';
import DeliveryTrackerStatus from './DeliveryTrackerStatus';
import DeliveryTracker from './DeliveryTracker';
import DeliveryTrackerCanceledOrderSection from './DeliveryTrackerCanceledOrderSection';

const OrderCompleteBody = memo(({
  shouldShowOdDummyTracker,
  isShowDeliveryTrackerMap,
  isShowOrderComplete,
  isScoober,
  isFutureOrder,
  isBarcodeOrder,
  deliveryMethod,
  arrivalOrderDate,
  arrivalOrderTime,
  isCurrentAddressIsDinningRoom,
  isDonation,
  isVoucherCard,
  orderDate,
  orderTotal,
}: {
  shouldShowOdDummyTracker?: boolean;
  isShowDeliveryTrackerMap?: boolean;
  isShowOrderComplete?: boolean;
  isScoober?: boolean;
  isFutureOrder?: boolean;
  isBarcodeOrder?: boolean;
  deliveryMethod: DeliveryMethods | null;
  arrivalOrderDate: string;
  arrivalOrderTime: string;
  isCurrentAddressIsDinningRoom?: boolean;
  isDonation?: boolean;
  isVoucherCard?: boolean;
  orderDate: string;
  orderTotal: number;
}) => {
  if (isVoucherCard) {
    return <VoucherCardOrderComplete orderTotal={orderTotal} orderDate={orderDate} />;
  }

  if (isShowDeliveryTrackerMap || shouldShowOdDummyTracker) {
    return <InProgressTracker trackerVariant={shouldShowOdDummyTracker ? TrackerVariants.OD : TrackerVariants.DELIVERY_TRACKER} />;
  }

  if (isShowOrderComplete) {
    return (
      <OrderComplete
        isScoober={isScoober}
        isFutureOrder={isFutureOrder}
        isBarcodeOrder={isBarcodeOrder}
        isOrderIsDelivery={deliveryMethod === DeliveryMethods.DELIVERY}
        arrivalOrderDate={arrivalOrderDate}
        arrivalOrderTime={arrivalOrderTime}
        isCurrentAddressIsDinningRoom={isCurrentAddressIsDinningRoom}
        isDonation={isDonation}
      />
    );
  }
  return null;
});

const MapAndOrderBlock = ({isDonation, isVoucherCard, shouldShowOdDummyTracker}: {
  isDonation?: boolean;
  isVoucherCard?: boolean;
  shouldShowOdDummyTracker?: boolean;
}) => {
  const all = useSelector(selectOrderSuccessData) || EMPTY_OBJECT;
  const {
    address,
    restaurant,
    deliveryMethod: deliveryMethodFromServer,
    isBarcodeOrder,
    arrivalOrderDate,
    arrivalOrderDateTimeText,
    arrivalOrderTime: _arrivalOrderTime,
    isFutureOrder,
    orderStatus,
    orderDate,
    orderTotal,
  } = all;

  const {status} = useSelector(selectUserLastOrderScooberJob) || {};

  const isTrackerUrlEmpty = useSelector(selectIsTrackerUrlEmpty);
  const isContrastActive = useSelector(selectA11yContrast);

  const deliveryMethod = getDeliveryMethodFromAPIString(deliveryMethodFromServer);
  const isScoober = restaurant?.isScoober;
  const isCurrentAddressIsDinningRoom = !!address?.shiftId;

  const isShowMap = useIsShowMap();
  const {isTracking: isShowDeliveryTracker, trackerUrl} = useIsShowDeliveryTrackerOrLink({isUsingEmptyUrl: true});

  const isOrderCancelled = orderStatus === 'Canceled';
  // while the status is undefined, the scoober map should not be presented
  const isShowDeliveryMap = isScoober && isShowMap && !isOrderCancelled;
  const isShowOrderComplete = isTrackerUrlEmpty || !isScoober;

  const arrivalOrderTime = isValidTimeRangeString(arrivalOrderDateTimeText) ?
    getTimeRangeLabel(arrivalOrderDateTimeText) :
    _arrivalOrderTime;

  return (
    <>
      {isShowDeliveryTracker && !isOrderCancelled && status && (
        <>
          <DeliveryTracker isContrastActive={isContrastActive} trackerUrl={trackerUrl} />
          <DeliveryTrackerStatus status={status} isShowDeliveryTracker={isShowDeliveryTracker} />
        </>
      )}

      {!isOrderCancelled && (
        <OrderCompleteBody
          arrivalOrderDate={arrivalOrderDate}
          arrivalOrderTime={arrivalOrderTime}
          isShowDeliveryTrackerMap={isShowDeliveryMap}
          shouldShowOdDummyTracker={shouldShowOdDummyTracker}
          isFutureOrder={isFutureOrder}
          isBarcodeOrder={isBarcodeOrder}
          isScoober={isScoober}
          isCurrentAddressIsDinningRoom={isCurrentAddressIsDinningRoom}
          isDonation={isDonation}
          deliveryMethod={deliveryMethod}
          isShowOrderComplete={isShowOrderComplete}
          isVoucherCard={isVoucherCard}
          orderDate={orderDate}
          orderTotal={orderTotal}
        />
      )}
      {isOrderCancelled && <DeliveryTrackerCanceledOrderSection />}
    </>
  );
};

export default memo(MapAndOrderBlock);
